import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Crown } from "lucide-react";
import { Button } from "../components/ui/button";
import { paymentStatus } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function PaymentResult({ cancel }) {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [params] = useSearchParams();
  const [state, setState] = useState(cancel ? "cancelled" : "checking");

  useEffect(() => {
    if (cancel) return;
    const sid = params.get("session_id");
    if (!sid) { setState("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const res = await paymentStatus(sid);
        if (res.payment_status === "paid") { await refreshUser(); setState("success"); return; }
        if (res.status === "expired" || res.payment_status === "failed") { setState("error"); return; }
      } catch (e) { console.warn("payment status poll failed:", e?.message || e); }
      if (tries++ < 12) setTimeout(poll, 2000); else setState("error");
    };
    poll();
  }, [cancel, params, refreshUser]);

  return (
    <div className="app-bg min-h-screen flex items-center justify-center px-6" data-testid="payment-result">
      <div className="max-w-md w-full text-center rounded-2xl bg-surface-card/50 border border-purple-500/20 p-10">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-purple-300 mx-auto" />
            <p className="mt-4 text-purple-100">Confirming your payment…</p>
          </>
        )}
        {state === "success" && (
          <>
            <div className="p-4 rounded-full bg-emerald-500/15 border border-emerald-400/30 inline-flex">
              <CheckCircle2 className="w-10 h-10 text-emerald-300" />
            </div>
            <h1 className="font-display text-2xl font-bold text-white mt-4 flex items-center justify-center gap-2">
              <Crown className="w-5 h-5 text-amber-300" /> You're Premium!
            </h1>
            <p className="text-purple-200/70 mt-2">Unlimited uploads, flashcards, and master Anki export are unlocked.</p>
            <Button data-testid="payment-continue-btn" onClick={() => navigate("/")}
              className="mt-6 rounded-full bg-purple-600 hover:bg-purple-500 px-7 h-12">Start exploring</Button>
          </>
        )}
        {state === "cancelled" && (
          <>
            <div className="p-4 rounded-full bg-amber-500/15 border border-amber-400/30 inline-flex">
              <XCircle className="w-10 h-10 text-amber-300" />
            </div>
            <h1 className="font-display text-2xl font-bold text-white mt-4">Checkout cancelled</h1>
            <p className="text-purple-200/70 mt-2">No charge was made. You can upgrade anytime.</p>
            <Button data-testid="payment-back-btn" onClick={() => navigate("/")}
              className="mt-6 rounded-full bg-purple-600 hover:bg-purple-500 px-7 h-12">Back to app</Button>
          </>
        )}
        {state === "error" && (
          <>
            <div className="p-4 rounded-full bg-rose-500/15 border border-rose-400/30 inline-flex">
              <XCircle className="w-10 h-10 text-rose-300" />
            </div>
            <h1 className="font-display text-2xl font-bold text-white mt-4">Couldn't confirm payment</h1>
            <p className="text-purple-200/70 mt-2">If you were charged, your plan will update shortly.</p>
            <Button data-testid="payment-back-btn" onClick={() => navigate("/")}
              className="mt-6 rounded-full bg-purple-600 hover:bg-purple-500 px-7 h-12">Back to app</Button>
          </>
        )}
      </div>
    </div>
  );
}
